import { Delete } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import { FieldArray } from "formik";
import React, { ReactElement, ReactNode, useMemo } from "react";
import * as yup from "yup";

import {
  EditTagInput,
  IntegrationServiceType,
  NgpVanDatabaseModeGqlType,
  NgpVanIntegration,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { FormikSelect } from "~frontend/components/forms/FormSelect";
import { FormikTextField } from "~frontend/components/forms/FormTextField";
import { FormikDialog } from "~frontend/components/forms/FormikDialog";
import FormikErrorBanner from "~frontend/components/forms/FormikErrorBanner";
import FormikNgpVanActionField, {
  NgpVanTagActionType,
} from "~frontend/components/forms/NgpVanObjectAutocomplete/FormikNgpVanActionField";
import { ListTagsWithIntegrationsTag } from "~frontend/graphql/tags";
import { useIntegrations } from "~frontend/hooks/useIntegrations";

type Values = {
  name: string;
  integrations: {
    integrationIdAndDbMode: string;
    actionType: NgpVanTagActionType | "";
    activistCodeId: number | undefined;
    resultCodeId: number | "";
    surveyQuestionId: number | undefined;
    surveyResponseId: number | "";
  }[];
};

function emptyIntegration(): Values["integrations"][number] {
  return {
    integrationIdAndDbMode: "",
    actionType: "",
    activistCodeId: undefined,
    resultCodeId: "",
    surveyQuestionId: undefined,
    surveyResponseId: "",
  };
}

function actionForModeToValues(
  actionForMode: ListTagsWithIntegrationsTag["integrationActions"][number]["ngpVanMyCampaignActions"][number]
): Omit<Values["integrations"][number], "integrationIdAndDbMode"> {
  switch (actionForMode.__typename) {
    case "NgpVanTagIntegrationActionApplyActivistCode":
      return {
        actionType: "APPLY_ACTIVIST_CODE",
        activistCodeId: actionForMode.activistCodeId,
        resultCodeId: "",
        surveyQuestionId: undefined,
        surveyResponseId: "",
      };
    case "NgpVanTagIntegrationActionApplyResultCode":
      return {
        actionType: "APPLY_RESULT_CODE",
        activistCodeId: undefined,
        resultCodeId: actionForMode.resultCodeId,
        surveyQuestionId: undefined,
        surveyResponseId: "",
      };
    case "NgpVanTagIntegrationActionApplySurveyResponse":
      return {
        actionType: "APPLY_SURVEY_RESPONSE",
        activistCodeId: undefined,
        resultCodeId: "",
        surveyQuestionId: actionForMode.surveyQuestionId,
        surveyResponseId: actionForMode.surveyResponseId,
      };
  }
}

function tagToValues(tag: ListTagsWithIntegrationsTag): Values {
  return {
    name: tag.name,
    integrations: tag.integrationActions.flatMap(action => {
      const integrations: Values["integrations"] = [];

      action.ngpVanMyVotersActions.forEach(ngpVanMyVotersAction => {
        integrations.push({
          integrationIdAndDbMode: `${action.integration.id}|${NgpVanDatabaseModeGqlType.MyVoters}`,
          ...actionForModeToValues(ngpVanMyVotersAction),
        });
      });

      action.ngpVanMyCampaignActions.forEach(ngpVanMyCampaignAction => {
        integrations.push({
          integrationIdAndDbMode: `${action.integration.id}|${NgpVanDatabaseModeGqlType.MyCampaign}`,
          ...actionForModeToValues(ngpVanMyCampaignAction),
        });
      });

      return integrations;
    }),
  };
}

function valuesToTagInput(tagId: string, values: Values): EditTagInput {
  return {
    name: values.name,
    tagId,
    ngpVanIntegrations:
      values.integrations.length === 0
        ? undefined
        : values.integrations.map(integration => ({
            integrationId: integration.integrationIdAndDbMode.split("|")[0],
            databaseMode: integration.integrationIdAndDbMode.split(
              "|"
            )[1] as NgpVanDatabaseModeGqlType,
            tagActionsJson: JSON.stringify([
              {
                actionType: integration.actionType,
                activistCodeId: integration.activistCodeId
                  ? integration.activistCodeId
                  : undefined,
                resultCodeId: integration.resultCodeId
                  ? integration.resultCodeId
                  : undefined,
                surveyQuestionId: integration.surveyQuestionId
                  ? integration.surveyQuestionId
                  : undefined,
                surveyResponseId: integration.surveyResponseId
                  ? integration.surveyResponseId
                  : undefined,
              },
            ]),
          })),
  };
}

export default function EditTagDialog({
  open,
  tag,
  onSubmit,
  onClose,
}: {
  open: boolean;
  tag: ListTagsWithIntegrationsTag;
  onSubmit: (values: EditTagInput) => Promise<void>;
  onClose: () => void;
}): ReactElement {
  const integrationQuery = useIntegrations({
    service: IntegrationServiceType.Ngpvan,
  });

  const integrationOptions = useMemo(() => {
    if (!integrationQuery.integrations) {
      return [];
    }

    return (integrationQuery.integrations as NgpVanIntegration[]).flatMap(
      integration => {
        const options: {
          integrationIdAndDbMode: string;
          label: string;
        }[] = [];

        if (integration.metadata.hasMyVoters) {
          options.push({
            integrationIdAndDbMode: `${integration.id}|${NgpVanDatabaseModeGqlType.MyVoters}`,
            label: `${integration.name} > MyVoters`,
          });
        }

        if (integration.metadata.hasMyCampaign) {
          options.push({
            integrationIdAndDbMode: `${integration.id}|${NgpVanDatabaseModeGqlType.MyCampaign}`,
            label: `${integration.name} > MyCampaign`,
          });
        }

        return options;
      }
    );
  }, [integrationQuery.integrations]);

  function integrationMenuItems(
    remainingOptions: {
      integrationIdAndDbMode: string;
      label: string;
    }[],
    fallbackValue: string
  ): ReactNode[] {
    if (integrationQuery.loading) {
      return [
        <MenuItem key="loading" value={fallbackValue} disabled>
          Loading...
        </MenuItem>,
      ];
    }

    if (integrationQuery.error) {
      return [
        <MenuItem key="error" value={fallbackValue} disabled>
          Error loading integrations
        </MenuItem>,
      ];
    }

    if (remainingOptions.length === 0) {
      return [
        <MenuItem key="no-integrations" value={fallbackValue} disabled>
          No integrations available
        </MenuItem>,
      ];
    }

    return remainingOptions.map(option => (
      <MenuItem
        key={option.integrationIdAndDbMode}
        value={option.integrationIdAndDbMode}
      >
        {option.label}
      </MenuItem>
    ));
  }

  return (
    <Dialog open={open} fullWidth maxWidth="md">
      <DialogContent>
        <FormikDialog
          open={open}
          fullWidth
          maxWidth="md"
          initialValues={tagToValues(tag)}
          onSubmit={async (values, formik) => {
            await onSubmit(valuesToTagInput(tag.id, values));
            formik.resetForm();
            onClose();
          }}
          validationSchema={yup.object().shape({
            name: yup.string().required("Required"),
            integrations: yup.array().of(
              yup.object().shape({
                integrationIdAndDbMode: yup.string().required("Required"),
                actionType: yup.string().required("Required"),
                activistCodeId: yup.string().when("actionType", {
                  is: "APPLY_ACTIVIST_CODE",
                  then: yup.string().required("Required"),
                }),
                resultCodeId: yup.string().when("actionType", {
                  is: "APPLY_RESULT_CODE",
                  then: yup.string().required("Required"),
                }),
                surveyQuestionId: yup.string().when("actionType", {
                  is: "APPLY_SURVEY_RESPONSE",
                  then: yup.string().required("Required"),
                }),
                surveyResponseId: yup.string().when("actionType", {
                  is: "APPLY_SURVEY_RESPONSE",
                  then: yup.string().required("Required"),
                }),
              })
            ),
          })}
        >
          {({ isSubmitting, values, setFieldTouched }) => {
            return (
              <>
                <DialogTitle>Edit Tag</DialogTitle>
                <DialogContent>
                  <FieldArray
                    name="integrations"
                    render={arrayHelpers => (
                      <>
                        <FormikErrorBanner />
                        <FormikTextField
                          fullWidth
                          label="Tag Name"
                          name="name"
                        />
                        {values.integrations.map((integrationValue, index) => {
                          let integrationId: string | undefined;
                          let databaseMode:
                            | NgpVanDatabaseModeGqlType
                            | undefined;
                          let integration: NgpVanIntegration | undefined;

                          if (integrationValue.integrationIdAndDbMode) {
                            [integrationId, databaseMode] =
                              integrationValue.integrationIdAndDbMode.split(
                                "|"
                              ) as [string, NgpVanDatabaseModeGqlType];

                            if (integrationQuery.integrations) {
                              integration = integrationQuery.integrations.find(
                                i => i.id === integrationId
                              ) as NgpVanIntegration | undefined;
                            }
                          }

                          return (
                            <React.Fragment key={index}>
                              {index > 0 && <hr />}
                              <FlexBox>
                                <Box sx={{ flex: 1 }}>
                                  <FormikSelect
                                    name={`integrations[${index}].integrationIdAndDbMode`}
                                    fullWidth
                                    label="Integration"
                                    sx={{ flex: 1 }}
                                    overrideFormikProps={field => ({
                                      ...field,
                                      onChange: (
                                        event: React.ChangeEvent<any>
                                      ) => {
                                        // instead of just setting the single
                                        // value, we replace the whole array
                                        // element and mark it untouched (since
                                        // everything has to be reconfigured when
                                        // you pick a new integration)
                                        arrayHelpers.replace(index, {
                                          ...emptyIntegration(),
                                          integrationIdAndDbMode:
                                            event.target.value,
                                        });
                                        setFieldTouched(
                                          `integrations[${index}]`,
                                          false
                                        ).catch(console.error);
                                      },
                                    })}
                                  >
                                    {integrationMenuItems(
                                      integrationOptions,
                                      integrationValue.integrationIdAndDbMode
                                    )}
                                  </FormikSelect>
                                </Box>
                                <Box>
                                  <IconButton
                                    onClick={arrayHelpers.handleRemove(index)}
                                    sx={{
                                      height: "40px",
                                      mt: 3,
                                      ml: 2,
                                    }}
                                  >
                                    <Delete />
                                  </IconButton>
                                </Box>
                              </FlexBox>
                              {integrationValue.integrationIdAndDbMode && (
                                <Box sx={{ marginRight: "52px" }}>
                                  {integration ? (
                                    <FormikNgpVanActionField
                                      databaseMode={databaseMode!}
                                      integrationId={integrationId!}
                                      integration={integration}
                                      namePrefix={`integrations[${index}].`}
                                    />
                                  ) : (
                                    <Typography>
                                      Loading integration details...
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </React.Fragment>
                          );
                        })}
                        {values.integrations.length > 0 && (
                          <Typography variant="body1">
                            Note: If an admin or texter deletes a tag after
                            applying it to a thread, the tag will only be
                            removed from Scale to Win’s data, and will not
                            remove the mapped VAN action.
                          </Typography>
                        )}
                        <DialogActions
                          sx={{ justifyContent: "space-between", px: 3 }}
                        >
                          <Box>
                            <Button
                              disabled={
                                isSubmitting ||
                                integrationOptions.every(option =>
                                  values.integrations.some(
                                    integration =>
                                      integration.integrationIdAndDbMode ===
                                      option.integrationIdAndDbMode
                                  )
                                )
                              }
                              onClick={arrayHelpers.handlePush(
                                emptyIntegration()
                              )}
                              color="primary"
                            >
                              + Add a VAN Integration
                            </Button>
                          </Box>
                          <Box>
                            <Button
                              disabled={isSubmitting}
                              onClick={onClose}
                              color="primary"
                            >
                              Cancel
                            </Button>
                            <Button
                              type="submit"
                              disabled={isSubmitting}
                              color={"primary"}
                              variant="contained"
                            >
                              {isSubmitting ? "Saving..." : "Save"}
                            </Button>
                          </Box>
                        </DialogActions>
                      </>
                    )}
                  />
                </DialogContent>
              </>
            );
          }}
        </FormikDialog>
      </DialogContent>
    </Dialog>
  );
}
